// ABOUTME: Reusable dialog to import a catalog/global config bundle — validate schema, then import.
// ABOUTME: Parameterized by validate/import callbacks so it serves both country configs and global metadata.
import {Check, Close, WarningAmber} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material'
import React, {ChangeEvent, useState} from 'react'

import type {ImportCompatibilityReport, ImportReport} from '../hooks/useCatalogImportExport'

interface CatalogBundleImportDialogProps {
  open: boolean
  onClose: () => void
  title: string
  busy: boolean
  onValidate: (bundle: unknown) => Promise<ImportCompatibilityReport>
  onImport: (args: {bundle: unknown; force?: boolean}) => Promise<ImportReport>
}

const ResultView: React.FC<{report: ImportReport}> = ({report}) => {
  const failed = report.results.filter((r) => r.status === 'Failed')
  return (
    <>
      <Alert severity={report.failed === 0 ? 'success' : 'warning'}>
        {`${report.created} created, ${report.updated} updated, ${report.failed} failed.`}
      </Alert>
      {failed.length > 0 && (
        <List dense>
          {failed.map((r) => (
            <ListItem key={r.scope} disablePadding>
              <ListItemText primary={r.scope} secondary={r.reason ?? ''} />
            </ListItem>
          ))}
        </List>
      )}
    </>
  )
}

const CompatView: React.FC<{compat: ImportCompatibilityReport}> = ({compat}) => {
  if (compat.compatible) {
    return (
      <Alert severity="success" icon={<Check fontSize="inherit" />} sx={{mt: 2}}>
        The file is compatible with this environment.
      </Alert>
    )
  }
  return (
    <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{mt: 2}}>
      The file may be incompatible with this environment:
      <List dense disablePadding>
        {compat.issues.map((issue) => (
          <ListItem key={issue} disablePadding>
            <ListItemText primary={issue} />
          </ListItem>
        ))}
      </List>
    </Alert>
  )
}

export const CatalogBundleImportDialog: React.FC<CatalogBundleImportDialogProps> = ({
  open,
  onClose,
  title,
  busy,
  onValidate,
  onImport
}) => {
  const [fileName, setFileName] = useState<string | null>(null)
  const [bundle, setBundle] = useState<unknown>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [compat, setCompat] = useState<ImportCompatibilityReport | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)

  const reset = () => {
    setFileName(null)
    setBundle(null)
    setParseError(null)
    setCompat(null)
    setReport(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    reset()
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setFileName(file.name)
    try {
      const parsed = JSON.parse(await file.text())
      setBundle(parsed)
      setCompat(await onValidate(parsed))
    } catch {
      setParseError('The selected file is not valid JSON.')
    }
  }

  const runImport = async (force: boolean) => {
    if (bundle === null) return
    setReport(await onImport({bundle, force}))
  }

  const canImport = compat?.compatible === true
  const canForce = compat !== null && !compat.compatible

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <DialogTitle>{title}</DialogTitle>
        <IconButton onClick={handleClose} sx={{mr: 1}} aria-label="Close">
          <Close />
        </IconButton>
      </Box>

      <DialogContent dividers>
        {report ? (
          <ResultView report={report} />
        ) : (
          <>
            <Button component="label" variant="outlined" disabled={busy}>
              Choose JSON file
              <input type="file" accept=".json,application/json" hidden onChange={handleFile} />
            </Button>
            {fileName && (
              <Typography variant="body2" sx={{mt: 1}}>
                {fileName}
              </Typography>
            )}
            {parseError && (
              <Alert severity="error" sx={{mt: 2}}>
                {parseError}
              </Alert>
            )}
            {compat && <CompatView compat={compat} />}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />}>
          {report ? 'Close' : 'Cancel'}
        </Button>
        {!report && canImport && (
          <Button variant="contained" startIcon={<Check />} disabled={busy} onClick={() => runImport(false)}>
            Import
          </Button>
        )}
        {!report && canForce && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<WarningAmber />}
            disabled={busy}
            onClick={() => runImport(true)}
          >
            Import anyway
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
